import { Clock, Lock } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { useConfirm } from "@/components/ConfirmDialog";
import { KebabMenu } from "@/components/KebabMenu";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { StatusPill } from "@/components/StatusPill";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { isEnabled } from "@/lib/enums";
import { formatDate } from "@/lib/formatDate";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { plural } from "@/lib/plural";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { hasRole } from "@/lib/hasRole";
import { InviteModal } from "./InviteModal";
import { MemberUsers } from "./users/MemberUsers";
import type { UserSummary } from "./users/shared";
import { LoadError } from "@/components/LoadError";
import { EmptyState } from "@/components/EmptyState";

interface OrganizationInvite {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  expiresAt: string;
}

const ROLE_RANK: Record<string, number> = { User: 0, Admin: 1, Owner: 2 };

const COLUMNS = [
  "User",
  "Role",
  "Teams",
  "Status",
  "Joined",
  "Last seen",
] as const;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 shrink-0 rounded-full bg-surface" />
              <div className="flex flex-col gap-1.5">
                <div className="h-3 w-32 rounded bg-surface" />
                <div className="h-2.5 w-40 rounded bg-surface" />
              </div>
            </div>
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-16 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-6 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-5 w-20 rounded-full bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
          <td />
        </tr>
      ))}
    </>
  );
}

export function Users() {
  const { user } = useAuth();

  return hasRole(user?.roles, "Admin") ? <AdminUsers /> : <MemberUsers />;
}

function AdminUsers() {
  const { accessToken, user } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [actioningUserId, setActioningUserId] = useState<string | null>(null);
  const { toast, showToast } = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [invites, setInvites] = useState<OrganizationInvite[] | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);
  const [ssoEnabled, setSsoEnabled] = useState<boolean | null>(null);

  const hasFilters = Boolean(debouncedNameFilter || roleFilter || statusFilter);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void api
      .get<{ status: number }>(`/organizations/${orgId}/sso`)
      .then((data) => {
        if (!cancelled) {
          setSsoEnabled(data ? isEnabled(data.status) : false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId]);

  const fetchInvites = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    return api.get<OrganizationInvite[]>(`/organizations/${orgId}/invites`);
  }, [accessToken, api, orgId]);

  useEffect(() => {
    let cancelled = false;

    void fetchInvites().then((data) => {
      if (!cancelled) {
        setInvites(data ?? []);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchInvites]);

  async function handleRevokeInvite(invite: OrganizationInvite) {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      `${invite.email} will no longer be able to join with this invite. You can send a new one at any time.`,
      {
        title: `Revoke the invite to ${invite.email}?`,
        confirmLabel: "Revoke",
      },
    );

    if (!confirmed) {
      return;
    }

    setRevokingInviteId(invite.id);

    try {
      await api.send(
        `/organizations/${orgId}/invites/${invite.id}`,
        "DELETE",
        undefined,
        "Couldn't revoke invite.",
      );

      showToast(`Invite to ${invite.email} revoked.`, "success");
      setInvites((await fetchInvites()) ?? []);
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't revoke invite.",
        "error",
      );
    } finally {
      setRevokingInviteId(null);
    }
  }

  const fetchUsers = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });

    if (debouncedNameFilter) {
      params.set("name", debouncedNameFilter);
    }

    if (roleFilter) {
      params.set("role", roleFilter);
    }

    if (statusFilter) {
      params.set("status", statusFilter);
    }

    return api.get<PagedResult<UserSummary>>(
      `/organizations/${orgId}/users?${params.toString()}`,
    );
  }, [
    accessToken,
    api,
    orgId,
    page,
    debouncedNameFilter,
    roleFilter,
    statusFilter,
  ]);

  const applyUsers = useCallback((data: PagedResult<UserSummary> | null) => {
    if (data) {
      setUsers(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } else {
      setError("Couldn't load users.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchUsers().then((data) => {
      if (!cancelled) {
        applyUsers(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchUsers, applyUsers]);

  function retry() {
    setError(null);
    setUsers(null);
    void fetchUsers().then(applyUsers);
  }

  function clearFilters() {
    setNameFilter("");
    setRoleFilter("");
    setStatusFilter("");
    setPage(1);
  }

  async function changeRole(u: UserSummary, role: string) {
    if (!accessToken || !orgId) {
      return;
    }

    setActioningUserId(u.id);

    try {
      await api.send(
        `/organizations/${orgId}/users/${u.id}/role`,
        "PATCH",
        { role },
        "Couldn't update role.",
      );

      showToast(`Role changed to ${role} for ${u.name}.`, "success");
      applyUsers(await fetchUsers());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't update role.",
        "error",
      );
    } finally {
      setActioningUserId(null);
    }
  }

  async function demoteRole(u: UserSummary, role: string) {
    const current = u.roles[0] ?? "User";

    const confirmed = await confirm(
      `${u.name} will lose all permissions granted by the ${current} role.`,
      {
        title: `Change ${u.name} from ${current} to ${role}?`,
        confirmLabel: "Change role",
      },
    );

    if (!confirmed) {
      applyUsers(await fetchUsers());
      return;
    }

    await changeRole(u, role);
  }

  async function transferOwnership(u: UserSummary) {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      `Making ${u.name} the owner of this organization will change your role to Admin`,
      {
        title: "Transfer ownership",
        confirmLabel: "Make Owner",
      },
    );

    if (!confirmed) {
      applyUsers(await fetchUsers());
      return;
    }

    setActioningUserId(u.id);

    try {
      await api.send(
        `/organizations/${orgId}/users/${u.id}/transfer-ownership`,
        "PATCH",
        undefined,
        "Couldn't transfer ownership.",
      );

      showToast(`${u.name} is now the owner. You are an Admin.`, "success");
      applyUsers(await fetchUsers());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't transfer ownership.",
        "error",
      );
    } finally {
      setActioningUserId(null);
    }
  }

  function handleRoleChange(u: UserSummary, role: string) {
    const current = u.roles[0] ?? "User";

    if (role === current) {
      return;
    }

    if (role === "Owner") {
      void transferOwnership(u);
    } else if ((ROLE_RANK[role] ?? 0) < (ROLE_RANK[current] ?? 0)) {
      void demoteRole(u, role);
    } else {
      void changeRole(u, role);
    }
  }

  async function toggleSuspended(u: UserSummary) {
    if (!accessToken || !orgId) {
      return;
    }

    if (!u.isSuspended) {
      const confirmed = await confirm(
        `${u.name} will be signed out and won't be able to sign in again until you reinstate them.`,
        {
          title: `Suspend ${u.name}?`,
          confirmLabel: "Suspend",
        },
      );

      if (!confirmed) {
        return;
      }
    }

    setActioningUserId(u.id);

    try {
      await api.send(
        `/organizations/${orgId}/users/${u.id}/${u.isSuspended ? "reinstate" : "suspend"}`,
        "PATCH",
        undefined,
        "Couldn't update user status.",
      );

      showToast(
        u.isSuspended
          ? `${u.name} is reinstated and can sign in again.`
          : `${u.name} is suspended and can't sign in.`,
        "success",
      );
      applyUsers(await fetchUsers());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't update user status.",
        "error",
      );
    } finally {
      setActioningUserId(null);
    }
  }

  function isManageable(u: UserSummary) {
    return !u.isSsoManaged && u.id !== user?.id && u.roles[0] !== "Owner";
  }

  async function deleteUser(u: UserSummary) {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      `${u.name} will be signed out and permanently removed from this organization.`,
      {
        title: `Delete ${u.name}?`,
        confirmLabel: "Delete",
      },
    );

    if (!confirmed) {
      return;
    }

    setActioningUserId(u.id);

    try {
      await api.send(
        `/organizations/${orgId}/users/${u.id}`,
        "DELETE",
        undefined,
        "Couldn't delete user.",
      );

      showToast(`${u.name} deleted.`, "success");
      applyUsers(await fetchUsers());
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't delete user.",
        "error",
      );
    } finally {
      setActioningUserId(null);
    }
  }

  const inviteButton = (
    <span
      title={
        ssoEnabled === true
          ? "Users are provisioned through your identity provider while SSO is enabled."
          : undefined
      }
    >
      <Button
        variant="primary"
        disabled={ssoEnabled !== false}
        onClick={() => setShowInviteModal(true)}
      >
        Invite users
      </Button>
    </span>
  );

  const pendingInvites = invites ?? [];
  const isLoading = users === null && !error;
  const isEmpty = users !== null && totalCount === 0;

  return (
    <div className="text-foreground">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          {users !== null && (
            <p className="mt-0.5 text-sm text-foreground/70">
              {plural(totalCount, "user")}
              {pendingInvites.length > 0 &&
                ` · ${plural(pendingInvites.length, "pending invite")}`}
            </p>
          )}
        </div>
        {inviteButton}
      </div>

      <div className="mb-4 flex gap-2">
        <SearchField
          value={nameFilter}
          onChange={(value) => {
            setNameFilter(value);
            setPage(1);
          }}
          label="Search users by name or email"
          placeholder="Search user name or email"
        />
        <Select
          aria-label="Filter by role"
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All roles</option>
          <option value="Owner">Owner</option>
          <option value="Admin">Admin</option>
          <option value="User">User</option>
        </Select>
        <Select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Suspended">Suspended</option>
        </Select>
      </div>

      {hasFilters && users !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(totalCount, "user")}</span>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {showInviteModal && (
        <InviteModal
          showToast={showToast}
          onClose={() => setShowInviteModal(false)}
          onSent={async () => setInvites((await fetchInvites()) ?? [])}
        />
      )}

      {error && <LoadError what="users" onRetry={retry} className="mb-4" />}

      {isEmpty ? (
        <EmptyState
          title={hasFilters ? "No users match these filters" : "No users yet"}
          description={
            hasFilters
              ? "Try a different name, role, or status."
              : "Invite someone to your organization to get started."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
                <th scope="col" className="border-b border-surface pb-2.5">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : (
                users?.map((u) => (
                  <tr
                    key={u.id}
                    className="h-14 border-b border-surface/70 hover:bg-surface"
                  >
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          name={u.name}
                          email={u.email}
                          avatarId={u.avatarId}
                          size="md"
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate">{u.name}</span>
                            {u.id === user?.id && <Chip>You</Chip>}
                            {u.isSsoManaged && (
                              <Chip>
                                <Lock className="h-2.5 w-2.5" strokeWidth={2} />
                                SSO
                              </Chip>
                            )}
                          </div>
                          <div className="truncate text-xs text-foreground/70">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      {u.id === user?.id || u.roles[0] === "Owner" ? (
                        <span className="inline-flex h-8 items-center border border-transparent px-2 text-foreground/70">
                          {u.roles.join(", ") || "None"}
                        </span>
                      ) : (
                        <select
                          aria-label={`Role for ${u.name}`}
                          value={u.roles[0] ?? "User"}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          disabled={actioningUserId === u.id}
                          className="h-8 rounded-md border border-transparent bg-transparent px-2 text-sm text-foreground hover:border-muted hover:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-50"
                        >
                          {user?.roles.includes("Owner") && (
                            <option value="Owner">Owner</option>
                          )}
                          <option value="Admin">Admin</option>
                          <option value="User">User</option>
                        </select>
                      )}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {u.teamCount}
                    </td>
                    <td className="py-2 pr-4">
                      {u.isSuspended ? (
                        <StatusPill variant="suspended">Suspended</StatusPill>
                      ) : (
                        <StatusPill variant="active">Active</StatusPill>
                      )}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDate(u.createdAt)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {u.lastSeenAt ? formatDate(u.lastSeenAt) : "Never"}
                    </td>
                    <td className="py-2 text-right">
                      <KebabMenu
                        label={`More actions for ${u.name}`}
                        items={[
                          {
                            label: u.isSuspended ? "Reinstate" : "Suspend",
                            onClick: () => toggleSuspended(u),
                            disabled:
                              actioningUserId === u.id || !isManageable(u),
                            danger: !u.isSuspended,
                          },
                          {
                            label: "Delete",
                            onClick: () => deleteUser(u),
                            disabled:
                              actioningUserId === u.id || !isManageable(u),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={Math.ceil(totalCount / PAGE_SIZE)}
        onPageChange={setPage}
      />

      {pendingInvites.length > 0 && (
        <section className="mt-8 border-t border-surface pt-5">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
            Pending invites
          </h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[32rem] text-left text-sm">
              <tbody>
                {pendingInvites.map((invite) => (
                  <tr
                    key={invite.id}
                    className="h-14 border-b border-surface hover:bg-surface"
                  >
                    <td className="py-2 pr-4">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="max-w-xs truncate">
                          {invite.email}
                        </span>
                        <Chip>
                          <Clock className="h-2.5 w-2.5" strokeWidth={2} />
                          Expires {formatDate(invite.expiresAt)}
                        </Chip>
                      </div>
                      <div className="text-xs text-foreground/70">
                        Invited as {invite.role}
                      </div>
                    </td>
                    <td className="w-full py-2 text-right">
                      <KebabMenu
                        label={`More actions for the invite to ${invite.email}`}
                        items={[
                          {
                            label: "Revoke",
                            onClick: () => handleRevokeInvite(invite),
                            disabled: revokingInviteId === invite.id,
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Toast toast={toast} />

      {confirmDialog}
    </div>
  );
}
